import { CapManageControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.tsx';
import { useContext, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
	PairTransaction,
	PairTransactionSchema,
} from '@dealstack/shared/lib/common/zod/domain/transaction/transactions.zod.ts';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import {
	getPairTransaction,
	pairTransaction,
} from '@dealstack/frontend/app/services/Transactions.service.ts';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';

export const usePairTransactionDrawerController = ({
	transactionOne,
	refreshTransactionsList,
	onClose,
	opened,
	isFromDetailsPage,
}) => {
	const tCapManage = useTranslationCapManage();
	const { personsSelectOptions } = useContext(CapManageControllerContext);

	const getPairTransactionRequest = useMutation({
		mutationFn: getPairTransaction,
	});
	const pairTransactionRequest = useMutation({
		mutationFn: pairTransaction,
	});

	useEffect(() => {
		if (opened && transactionOne) {
			getPairTransactionRequest.mutate({
				id: transactionOne.id,
				securityId: transactionOne.securityId,
				units: Number(transactionOne.units),
				cost: Number(transactionOne.cost),
				date: transactionOne.date,
				sourceId: transactionOne.sourceId,
				destinationId: transactionOne.destinationId,
			});
		}
	}, [opened, transactionOne]);

	useEffect(() => {
		if (transactionOne) {
			pairTransactionForm.reset({
				transactionOneId: transactionOne.id,
				transactionTwoId: '',
			});
		}
	}, [transactionOne]);

	const pairTransactionForm = useForm<PairTransaction>({
		mode: 'all',
		resolver: zodResolver(PairTransactionSchema),
		defaultValues: {
			transactionOneId: transactionOne.id,
			transactionTwoId: '',
		},
		shouldFocusError: false,
	});

	const onSubmit = (formValues: PairTransaction) => {
		if (!formValues.transactionTwoId) {
			showToastV2({
				title: tCapManage('toasts.success.transaction_no_pair'),
				state: 'error',
			});
			return;
		}

		pairTransactionRequest.mutate(
			{
				transactionOneId: transactionOne.id,
				transactionTwoId: formValues.transactionTwoId,
			},
			{
				onSuccess: (response) => {
					if (refreshTransactionsList) {
						if (isFromDetailsPage && response?.newTransactionId) {
							refreshTransactionsList(response.newTransactionId);
						} else {
							refreshTransactionsList();
						}
					}
					showToastV2({
						title: tCapManage('toasts.success.transaction_pair_successfully'),
					});
					if (onClose) {
						onClose();
					}
				},
			}
		);
	};

	return {
		onSubmit,
		pairTransactionForm,
		personsSelectOptions,
		pairTransactions: getPairTransactionRequest.data,
		pairTransactionRequest,
		drawerLoading: getPairTransactionRequest.isPending,
	};
};
